/**
 * WebCodecsPlayer — Frame-accurate video player using WebCodecs API.
 *
 * For local files: all encoded samples are loaded into memory at once.
 * For remote URLs: only the moov atom (metadata) is fetched initially;
 * encoded sample data is fetched on-demand via HTTP range requests.
 * This allows near-instant start for large remote videos.
 *
 * Decoded VideoFrames are converted to ImageBitmaps immediately to
 * release GPU texture slots, allowing a much larger decode buffer
 * (~100 frames) than raw VideoFrame storage permits (~10).
 * During playback, if the decoder can't keep up we skip frames
 * instead of stalling.  Seeking / stepping accesses every frame.
 */

import { createFile as createMP4BoxFile, DataStream } from 'mp4box';

/**
 * In local dev, cross-origin fetches to Azure Blob Storage are blocked by
 * CORS.  Route them through the dev-server's /cors-proxy/ endpoint.
 */
function proxyUrl(url) {
  if (
    typeof window !== 'undefined' &&
    window.location.hostname === 'localhost' &&
    url.startsWith('http')
  ) {
    return `/cors-proxy/${encodeURIComponent(url)}`;
  }
  return url;
}

export class WebCodecsPlayer {
  constructor() {
    // DOM elements
    this.canvas = null;
    this.ctx = null;

    // Video metadata
    this.width = 0;
    this.height = 0;
    this.duration = 0;
    this.fps = 0;
    this.frameCount = 0;
    this.frameTimestamps = []; // PTS in seconds, indexed by presentation order

    // Internals
    this._samples = [];       // encoded samples in decode order
    this._frameInfo = [];     // { pts, isSync, decodeIndex, presentationIndex }
    this._timescale = 0;
    this._decoder = null;
    this._codecConfig = null;

    // Remote on-demand streaming
    this._isRemote = false;
    this._remoteUrl = null;

    // State
    this._isPaused = true;
    this._currentFrame = -1;
    this._isReady = false;

    // Seeking
    this._isSeeking = false;
    this._seekTarget = -1;

    // Frame buffer (presentationIndex → ImageBitmap)
    // ImageBitmaps don't hold GPU decoder slots, so we can buffer generously.
    this._frameBuffer = new Map();
    this._maxBufferSize = 100;
    this._maxBufferBehind = 10;
    this._maxDecodeQueueSize = 40;

    // Pending ImageBitmap conversions (tracked so we can await them after flush)
    this._pendingBitmaps = new Set();

    // Waiters: code that needs a specific frame can await _waitForFrame(idx)
    this._frameWaiters = new Map();

    // Set after decoder flush() to ensure the next fed sample is a keyframe
    this._decoderNeedsKeyframe = false;

    // Seek serialization: prevents overlapping async seeks from corrupting state
    this._seekInProgress = false;
    this._pendingSeek = null;

    // Sequential feed cursor (decode-order index)
    this._nextFeedIndex = 0;

    // Playback — wall-clock based (independent of audio)
    this._rafId = null;
    this._playStartWallTime = 0;
    this._playStartPTS = 0;
    this._playbackRate = 1;

    // Callbacks
    this.onTimeUpdate = null;
    this.onReady = null;
    this.onEnded = null;
    this.onPlay = null;
    this.onPause = null;
  }

  // ─── Public getters ────────────────────────────────────────────────

  get currentFrame() { return this._currentFrame; }

  get currentTime() {
    if (this._currentFrame >= 0 && this._currentFrame < this.frameTimestamps.length) {
      return this.frameTimestamps[this._currentFrame];
    }
    return 0;
  }

  get isPaused() { return this._isPaused; }
  get isReady() { return this._isReady; }

  get videoWidth() { return this.width; }
  get videoHeight() { return this.height; }

  // ─── Initialisation ────────────────────────────────────────────────

  async init(source, canvas) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');

    await this._parseMp4(source);

    this._createDecoder();

    this.canvas.width = this.width;
    this.canvas.height = this.height;

    this._isReady = true;

    await this.seekToFrame(0);

    if (this.onReady) this.onReady();

    console.log(
      `WebCodecs player ready: ${this.frameCount} frames, ` +
      `${this.fps.toFixed(2)} fps, ${this.width}×${this.height}, ` +
      `codec ${this._codecConfig.codec}` +
      (this._isRemote ? ' (on-demand streaming)' : '')
    );
  }

  // ─── MP4 parsing ───────────────────────────────────────────────────

  async _parseMp4(source) {
    // Local File/Blob: read everything at once (fast, no network)
    if (source instanceof File || source instanceof Blob) {
      const buffer = await source.arrayBuffer();
      return this._parseMp4Buffer(buffer);
    }

    // Remote URL: try on-demand (moov-only), fall back to full download
    if (typeof source === 'string') {
      return this._parseMp4Remote(source);
    }

    throw new Error('WebCodecsPlayer: unsupported source type');
  }

  /** Parse a complete ArrayBuffer through MP4Box (local files) */
  _parseMp4Buffer(buffer) {
    return new Promise((resolve, reject) => {
      const mp4boxFile = createMP4BoxFile();

      mp4boxFile.onReady = (info) => {
        try {
          const videoTrack = info.videoTracks?.[0]
            || info.tracks?.find(t => t.type === 'video');
          if (!videoTrack) { reject(new Error('No video track')); return; }

          this.width = videoTrack.video.width;
          this.height = videoTrack.video.height;
          this.duration = info.duration / info.timescale;
          this._timescale = videoTrack.timescale;

          const trak = mp4boxFile.getTrackById(videoTrack.id);
          this._codecConfig = {
            codec: videoTrack.codec,
            codedWidth: this.width,
            codedHeight: this.height,
            description: this._getCodecDescription(trak),
          };

          const collectedSamples = [];
          mp4boxFile.setExtractionOptions(videoTrack.id, null, {
            nbSamples: Infinity,
          });

          mp4boxFile.onSamples = (_id, _ref, samples) => {
            for (const s of samples) {
              collectedSamples.push({
                data: new Uint8Array(s.data),
                cts: s.cts,
                dts: s.dts,
                duration: s.duration,
                isSync: s.is_sync,
              });
            }
          };

          mp4boxFile.start();

          if (collectedSamples.length === 0) {
            reject(new Error('No samples extracted'));
            return;
          }

          this._processSamples(collectedSamples);
          resolve();
        } catch (e) { reject(e); }
      };

      mp4boxFile.onError = (e) => reject(new Error(`MP4 parse error: ${e}`));

      buffer.fileStart = 0;
      mp4boxFile.appendBuffer(buffer);
      mp4boxFile.flush();
    });
  }

  /**
   * Remote URL: fetch only the moov atom to get metadata + sample table.
   * Encoded sample data is NOT downloaded up-front — it's fetched on-demand
   * via HTTP range requests when seeking or playing.
   *
   * Falls back to full streaming download if the server doesn't support
   * range requests or the moov atom can't be located in the probes.
   */
  async _parseMp4Remote(url) {
    const fetchUrl = proxyUrl(url);

    // ── Check server capabilities ──────────────────────────────────
    const headResp = await fetch(fetchUrl, { method: 'HEAD' });
    if (!headResp.ok) throw new Error(`HEAD request failed: ${headResp.status}`);

    const totalSize = parseInt(headResp.headers.get('content-length') || '0', 10);
    const supportsRange = headResp.headers.get('accept-ranges') === 'bytes';

    if (!totalSize || !supportsRange) {
      console.log('Server does not support range requests — downloading full video');
      return this._parseMp4Streaming(fetchUrl);
    }

    // ── Locate the moov atom via top-level box header walking ─────
    // MP4 files consist of top-level boxes (atoms): ftyp, mdat, moov, etc.
    // Each box starts with [4-byte size][4-byte type]. We walk the headers
    // to find moov's exact offset + size, then fetch only that range.
    // This avoids fixed-size probes that fail on large moov atoms.

    const HEADER_PROBE = 64 * 1024; // 64 KB — enough to read ftyp + box headers
    const headerEnd = Math.min(HEADER_PROBE - 1, totalSize - 1);
    const headerResp = await fetch(fetchUrl, {
      headers: { Range: `bytes=0-${headerEnd}` },
    });
    const headerBuf = await headerResp.arrayBuffer();
    const headerView = new DataView(headerBuf);

    // Walk top-level boxes to find moov's file offset and size
    let moovOffset = -1;
    let moovSize = -1;
    let boxPos = 0;
    while (boxPos + 8 <= headerBuf.byteLength && boxPos < totalSize) {
      let boxSize = headerView.getUint32(boxPos, false); // big-endian
      const boxType = String.fromCharCode(
        headerView.getUint8(boxPos + 4),
        headerView.getUint8(boxPos + 5),
        headerView.getUint8(boxPos + 6),
        headerView.getUint8(boxPos + 7),
      );

      // Extended size: real size is in the next 8 bytes (64-bit)
      if (boxSize === 1 && boxPos + 16 <= headerBuf.byteLength) {
        const hi = headerView.getUint32(boxPos + 8, false);
        const lo = headerView.getUint32(boxPos + 12, false);
        boxSize = hi * 0x100000000 + lo;
      }
      // boxSize === 0 means "extends to end of file"
      if (boxSize === 0) boxSize = totalSize - boxPos;

      if (boxType === 'moov') {
        moovOffset = boxPos;
        moovSize = boxSize;
        break;
      }

      boxPos += boxSize;
    }

    // If moov wasn't found in the header probe, it's likely after a large mdat.
    // boxPos now points to where the next unread box starts. We can probe
    // just enough bytes at that offset (or at the tail) to read the next header.
    if (moovOffset < 0 && boxPos > headerBuf.byteLength && boxPos < totalSize) {
      // We know the next box starts at boxPos — fetch just its header
      const nextHeaderEnd = Math.min(boxPos + 15, totalSize - 1);
      const nhResp = await fetch(fetchUrl, {
        headers: { Range: `bytes=${boxPos}-${nextHeaderEnd}` },
      });
      const nhBuf = await nhResp.arrayBuffer();
      const nhView = new DataView(nhBuf);
      if (nhBuf.byteLength >= 8) {
        let bSize = nhView.getUint32(0, false);
        const bType = String.fromCharCode(
          nhView.getUint8(4), nhView.getUint8(5),
          nhView.getUint8(6), nhView.getUint8(7),
        );
        if (bSize === 1 && nhBuf.byteLength >= 16) {
          bSize = nhView.getUint32(8, false) * 0x100000000 + nhView.getUint32(12, false);
        }
        if (bSize === 0) bSize = totalSize - boxPos;
        if (bType === 'moov') {
          moovOffset = boxPos;
          moovSize = bSize;
        } else {
          // Keep walking — there may be free/skip boxes between mdat and moov
          let nextPos = boxPos + bSize;
          while (moovOffset < 0 && nextPos < totalSize) {
            const probeEnd2 = Math.min(nextPos + 15, totalSize - 1);
            const r2 = await fetch(fetchUrl, {
              headers: { Range: `bytes=${nextPos}-${probeEnd2}` },
            });
            const b2 = await r2.arrayBuffer();
            if (b2.byteLength < 8) break;
            const v2 = new DataView(b2);
            let s2 = v2.getUint32(0, false);
            const t2 = String.fromCharCode(
              v2.getUint8(4), v2.getUint8(5), v2.getUint8(6), v2.getUint8(7),
            );
            if (s2 === 1 && b2.byteLength >= 16) {
              s2 = v2.getUint32(8, false) * 0x100000000 + v2.getUint32(12, false);
            }
            if (s2 === 0) s2 = totalSize - nextPos;
            if (t2 === 'moov') {
              moovOffset = nextPos;
              moovSize = s2;
            }
            nextPos += s2;
          }
        }
      }
    }

    if (moovOffset < 0) {
      console.log('Could not locate moov atom — falling back to full download');
      return this._parseMp4Streaming(fetchUrl);
    }

    console.log(`Moov atom found at offset ${moovOffset}, size ${(moovSize / (1024*1024)).toFixed(1)} MB`);

    // ── Fetch the data MP4Box needs: everything from file start through end of moov
    const mp4boxFile = createMP4BoxFile();

    let resolveReady, rejectReady;
    const readyPromise = new Promise((resolve, reject) => {
      resolveReady = resolve;
      rejectReady = reject;
    });

    mp4boxFile.onReady = (info) => resolveReady(info);
    mp4boxFile.onError = (e) => rejectReady(new Error(`MP4 parse error: ${e}`));

    // We need ftyp + moov (and any boxes between them) for MP4Box to parse correctly.
    // The simplest approach: fetch from 0 to end-of-moov in one range request.
    // For web-optimized files (moov near start) this is very small.
    // For non-optimized files, moov is at the end — we fetch from moovOffset to EOF
    // but also need the ftyp from the start. Feed them as two appends.
    const moovEnd = moovOffset + moovSize - 1;

    if (moovOffset <= headerBuf.byteLength) {
      // moov starts within our initial header probe — fetch from 0 to end of moov
      const fetchEnd = Math.min(moovEnd, totalSize - 1);
      const moovResp = await fetch(fetchUrl, {
        headers: { Range: `bytes=0-${fetchEnd}` },
      });
      const moovBuf = await moovResp.arrayBuffer();
      moovBuf.fileStart = 0;
      mp4boxFile.appendBuffer(moovBuf);
    } else {
      // moov is deep in the file — feed ftyp/header first, then moov range
      headerBuf.fileStart = 0;
      mp4boxFile.appendBuffer(headerBuf);

      const moovResp = await fetch(fetchUrl, {
        headers: { Range: `bytes=${moovOffset}-${Math.min(moovEnd, totalSize - 1)}` },
      });
      const moovBuf = await moovResp.arrayBuffer();
      moovBuf.fileStart = moovOffset;
      mp4boxFile.appendBuffer(moovBuf);
    }

    // ── Extract metadata from moov ─────────────────────────────────
    const info = await readyPromise;

    const videoTrack = info.videoTracks?.[0]
      || info.tracks?.find(t => t.type === 'video');
    if (!videoTrack) throw new Error('No video track');

    this.width = videoTrack.video.width;
    this.height = videoTrack.video.height;
    this.duration = info.duration / info.timescale;
    this._timescale = videoTrack.timescale;

    const trak = mp4boxFile.getTrackById(videoTrack.id);
    this._codecConfig = {
      codec: videoTrack.codec,
      codedWidth: this.width,
      codedHeight: this.height,
      description: this._getCodecDescription(trak),
    };

    // Build sample metadata from the parsed sample table.
    // trak.samples is populated entirely from the moov atom — no mdat needed.
    if (!trak.samples || trak.samples.length === 0) {
      throw new Error('No samples found in video track');
    }

    const samples = trak.samples.map(s => ({
      byteOffset: s.offset,
      byteSize: s.size,
      cts: s.cts,
      dts: s.dts,
      duration: s.duration,
      isSync: s.is_sync,
      data: null, // fetched on-demand via range requests
    }));

    this._processSamples(samples);

    // Mark as remote for on-demand fetching
    this._isRemote = true;
    this._remoteUrl = fetchUrl;

  }

  /** Streaming fallback: download full video progressively (no range support) */
  async _parseMp4Streaming(url) {
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`Fetch failed: ${resp.status} ${resp.statusText}`);

    const totalBytes = parseInt(resp.headers.get('content-length') || '0', 10);
    const reader = resp.body.getReader();

    return new Promise((resolve, reject) => {
      const mp4boxFile = createMP4BoxFile();
      let fileOffset = 0;
      let resolved = false;
      const collectedSamples = [];

      mp4boxFile.onReady = (info) => {
        try {
          const videoTrack = info.videoTracks?.[0]
            || info.tracks?.find(t => t.type === 'video');
          if (!videoTrack) { reject(new Error('No video track')); return; }

          this.width = videoTrack.video.width;
          this.height = videoTrack.video.height;
          this.duration = info.duration / info.timescale;
          this._timescale = videoTrack.timescale;

          const trak = mp4boxFile.getTrackById(videoTrack.id);
          this._codecConfig = {
            codec: videoTrack.codec,
            codedWidth: this.width,
            codedHeight: this.height,
            description: this._getCodecDescription(trak),
          };

          mp4boxFile.onSamples = (_id, _ref, samples) => {
            for (const s of samples) {
              collectedSamples.push({
                data: new Uint8Array(s.data),
                cts: s.cts,
                dts: s.dts,
                duration: s.duration,
                isSync: s.is_sync,
              });
            }
          };

          mp4boxFile.setExtractionOptions(videoTrack.id, null, {
            nbSamples: Infinity,
          });
          mp4boxFile.start();
        } catch (e) { reject(e); }
      };

      mp4boxFile.onError = (e) => reject(new Error(`MP4 parse error: ${e}`));

      const pump = async () => {
        try {
          while (true) {
            const { done, value } = await reader.read();

            if (done) {
              mp4boxFile.flush();
              if (collectedSamples.length === 0) {
                reject(new Error('No samples extracted'));
                return;
              }
              this._processSamples(collectedSamples);
              resolved = true;
              resolve();
              return;
            }

            const chunk = value.buffer.byteLength === value.byteLength
              ? value.buffer
              : value.buffer.slice(value.byteOffset, value.byteOffset + value.byteLength);
            chunk.fileStart = fileOffset;
            fileOffset += value.byteLength;

            mp4boxFile.appendBuffer(chunk);
          }
        } catch (e) {
          if (!resolved) reject(e);
        }
      };

      pump();
    });
  }

  _getCodecDescription(trak) {
    const entry = trak.mdia.minf.stbl.stsd.entries[0];
    const box = entry.avcC || entry.hvcC || entry.vpcC || entry.av1C;
    if (!box) return undefined;

    const stream = new DataStream(undefined, 0, DataStream.BIG_ENDIAN);
    box.write(stream);
    return new Uint8Array(stream.buffer, 8);
  }

  _processSamples(samples) {
    this._samples = samples;

    const frames = samples.map((s, decodeIndex) => ({
      pts: s.cts / this._timescale,
      isSync: s.isSync,
      decodeIndex,
    }));

    frames.sort((a, b) => a.pts - b.pts);
    frames.forEach((f, i) => { f.presentationIndex = i; });

    this._frameInfo = frames;
    this.frameTimestamps = frames.map(f => f.pts);
    this.frameCount = frames.length;

    if (this.frameCount >= 2) {
      const span = this.frameTimestamps[this.frameCount - 1] - this.frameTimestamps[0];
      this.fps = (this.frameCount - 1) / span;
    }
  }

  // ─── On-demand sample fetching (remote only) ──────────────────────

  /**
   * Fetch encoded data for samples[startIdx..endIdx] (decode order) via
   * HTTP range request(s).  Samples that already have data are skipped.
   */
  async _fetchSampleData(startIdx, endIdx) {
    if (!this._isRemote) return;

    // Collect samples that still need data
    let minOffset = Infinity;
    let maxEnd = 0;
    const needed = [];

    for (let i = startIdx; i <= endIdx; i++) {
      const s = this._samples[i];
      if (s.data !== null) continue; // already fetched
      needed.push(i);
      minOffset = Math.min(minOffset, s.byteOffset);
      maxEnd = Math.max(maxEnd, s.byteOffset + s.byteSize);
    }

    if (needed.length === 0) return;

    const resp = await fetch(this._remoteUrl, {
      headers: { Range: `bytes=${minOffset}-${maxEnd - 1}` },
    });

    if (!resp.ok && resp.status !== 206) {
      throw new Error(`Range request failed: ${resp.status}`);
    }

    const buffer = new Uint8Array(await resp.arrayBuffer());

    for (const i of needed) {
      const s = this._samples[i];
      const localStart = s.byteOffset - minOffset;
      s.data = buffer.slice(localStart, localStart + s.byteSize);
    }
  }

  /**
   * Background loop that fetches sample data ahead of the playback cursor.
   * Runs while playing; stops when paused or destroyed.
   */
  async _fetchAheadLoop() {
    const BATCH = 60; // ~2 seconds at 30 fps

    while (!this._isPaused && this._isRemote) {
      // Find the next sample without data, at or ahead of the feed cursor
      let start = this._nextFeedIndex;
      while (start < this._samples.length && this._samples[start].data !== null) {
        start++;
      }

      if (start >= this._samples.length) break; // everything fetched

      const end = Math.min(start + BATCH - 1, this._samples.length - 1);

      try {
        await this._fetchSampleData(start, end);
      } catch (e) {
        console.warn('Fetch-ahead error:', e);
        break;
      }

      // Let the render loop pick up newly available frames
      this._topUpDecoder();

      // Yield briefly so the render loop isn't starved
      await new Promise(r => setTimeout(r, 16));
    }
  }

  // ─── VideoDecoder ──────────────────────────────────────────────────

  _createDecoder() {
    if (this._decoder && this._decoder.state !== 'closed') {
      this._decoder.close();
    }

    this._decoder = new VideoDecoder({
      output: (frame) => this._onDecodedFrame(frame),
      error: (e) => console.error('VideoDecoder error:', e),
    });

    this._decoder.configure(this._codecConfig);
    this._decoderNeedsKeyframe = true;
  }

  /**
   * Decoder output callback.  Converts VideoFrame → ImageBitmap
   * immediately so the GPU decoder texture slot is released.
   * Frames behind the playback head or seeking intermediates are closed.
   */
  _onDecodedFrame(videoFrame) {
    const ptsSecs = videoFrame.timestamp / 1_000_000;
    const frameIdx = this._findFrameByPTS(ptsSecs);

    if (frameIdx === -1 || (!this._isSeeking && frameIdx < this._currentFrame - this._maxBufferBehind)) {
      videoFrame.close();
      return;
    }

    if (this._isSeeking && frameIdx !== this._seekTarget) {
      videoFrame.close();
      return;
    }

    const p = createImageBitmap(videoFrame).then((bitmap) => {
      videoFrame.close();
      this._storeFrame(frameIdx, bitmap);
      if (this._isSeeking && frameIdx === this._seekTarget) {
        this._isSeeking = false;
      }
      this._pendingBitmaps.delete(p);
    }).catch(() => {
      videoFrame.close();
      this._pendingBitmaps.delete(p);
    });
    this._pendingBitmaps.add(p);
  }

  _findFrameByPTS(ptsSecs) {
    let lo = 0, hi = this.frameCount - 1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      const diff = this.frameTimestamps[mid] - ptsSecs;
      if (Math.abs(diff) < 0.000005) return mid;
      if (diff < 0) lo = mid + 1; else hi = mid - 1;
    }
    if (lo < this.frameCount && Math.abs(this.frameTimestamps[lo] - ptsSecs) < 0.001) return lo;
    if (hi >= 0 && Math.abs(this.frameTimestamps[hi] - ptsSecs) < 0.001) return hi;
    return -1;
  }

  _storeFrame(idx, bitmap) {
    const old = this._frameBuffer.get(idx);
    if (old) { try { old.close(); } catch (_) {} }
    this._frameBuffer.set(idx, bitmap);

    const waiter = this._frameWaiters.get(idx);
    if (waiter) {
      this._frameWaiters.delete(idx);
      waiter();
    }
  }

  _waitForFrame(targetFrame, timeoutMs = 3000) {
    if (this._frameBuffer.has(targetFrame)) return Promise.resolve();

    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        this._frameWaiters.delete(targetFrame);
        reject(new Error(`Timed out waiting for frame ${targetFrame}`));
      }, timeoutMs);

      this._frameWaiters.set(targetFrame, () => {
        clearTimeout(timer);
        resolve();
      });
    });
  }

  async _flushAndWait() {
    this._decoderNeedsKeyframe = true;
    if (this._decoder.state === 'configured') {
      await this._decoder.flush();
    }
    if (this._pendingBitmaps.size > 0) {
      await Promise.all(this._pendingBitmaps);
    }
  }

  _displayFrame(idx) {
    const frame = this._frameBuffer.get(idx);
    if (!frame) return;

    this.ctx.drawImage(frame, 0, 0, this.width, this.height);
    this._currentFrame = idx;

    if (this.onTimeUpdate) this.onTimeUpdate(this.currentTime);
  }

  // ─── Sample feeding ────────────────────────────────────────────────

  _feedSample(decodeIndex) {
    if (decodeIndex < 0 || decodeIndex >= this._samples.length) return;
    const s = this._samples[decodeIndex];
    if (!s.data) return; // not yet fetched (remote) — safety guard

    if (this._decoderNeedsKeyframe && !s.isSync) return;

    this._decoder.decode(new EncodedVideoChunk({
      type: s.isSync ? 'key' : 'delta',
      timestamp: Math.round(s.cts * 1_000_000 / this._timescale),
      duration: Math.round(s.duration * 1_000_000 / this._timescale),
      data: s.data,
    }));

    if (s.isSync) this._decoderNeedsKeyframe = false;
  }

  /**
   * Feed samples to keep a look-ahead buffer.
   * Respects both decoder queue depth and ImageBitmap buffer count.
   * If the decoder was recently flushed, rewinds to the nearest keyframe
   * so the first fed sample is always a key chunk.
   */
  _topUpDecoder() {
    if (this._decoderNeedsKeyframe) {
      this._nextFeedIndex = this._findKeyframeBefore(this._nextFeedIndex);
    }

    while (
      this._nextFeedIndex < this._samples.length &&
      this._decoder.decodeQueueSize < this._maxDecodeQueueSize &&
      this._frameBuffer.size + this._pendingBitmaps.size < this._maxBufferSize
    ) {
      if (this._isRemote && !this._samples[this._nextFeedIndex].data) break;
      this._feedSample(this._nextFeedIndex++);
    }
  }

  _findKeyframeBefore(decodeIndex) {
    let kf = decodeIndex;
    while (kf > 0 && !this._samples[kf].isSync) kf--;
    return kf;
  }

  // ─── Seeking ───────────────────────────────────────────────────────

  async seekToFrame(targetFrame) {
    targetFrame = Math.max(0, Math.min(targetFrame, this.frameCount - 1));

    // Already buffered? Display immediately (synchronous, no contention).
    if (this._frameBuffer.has(targetFrame)) {
      this._displayFrame(targetFrame);
      return;
    }

    // Serialize async seeks — if one is already running, queue the latest
    // target and let the running seek pick it up when it finishes.
    if (this._seekInProgress) {
      this._pendingSeek = targetFrame;
      return;
    }

    this._seekInProgress = true;
    try {
      await this._seekToFrameInternal(targetFrame);
    } finally {
      this._seekInProgress = false;

      if (this._pendingSeek !== null) {
        const next = this._pendingSeek;
        this._pendingSeek = null;
        this.seekToFrame(next); // intentionally fire-and-forget
      }
    }
  }

  async _seekToFrameInternal(targetFrame) {
    // Re-check buffer (may have been populated while queued)
    if (this._frameBuffer.has(targetFrame)) {
      this._displayFrame(targetFrame);
      return;
    }

    const info = this._frameInfo[targetFrame];

    // Fast path: small forward step — feed sequentially without flushing.
    // The decoder still holds its reference chain so delta frames decode fine.
    if (!this._decoderNeedsKeyframe &&
        info.decodeIndex >= this._nextFeedIndex &&
        info.decodeIndex < this._nextFeedIndex + 5) {
      if (this._isRemote) {
        await this._fetchSampleData(this._nextFeedIndex, info.decodeIndex);
      }
      for (let i = this._nextFeedIndex; i <= info.decodeIndex; i++) {
        this._feedSample(i);
      }
      this._nextFeedIndex = info.decodeIndex + 1;

      try {
        await this._waitForFrame(targetFrame);
        this._displayFrame(targetFrame);
      } catch (_) {
        // Bitmap conversion timed out — ignore, user will see on next step
      }
      return;
    }

    // Full seek: flush decoder, decode from nearest keyframe → target
    this._clearBuffer();
    this._decoderNeedsKeyframe = true;
    if (this._decoder.state === 'configured') {
      await this._decoder.flush();
    } else {
      this._createDecoder();
    }

    const kf = this._findKeyframeBefore(info.decodeIndex);

    if (this._isRemote) {
      await this._fetchSampleData(kf, info.decodeIndex);
    }

    this._isSeeking = true;
    this._seekTarget = targetFrame;

    for (let i = kf; i <= info.decodeIndex; i++) this._feedSample(i);
    await this._flushAndWait();

    this._isSeeking = false;
    if (this._frameBuffer.has(targetFrame)) {
      this._displayFrame(targetFrame);
    }

    this._nextFeedIndex = info.decodeIndex + 1;
  }

  async seekToTime(time) {
    await this.seekToFrame(this.getFrameForTime(time));
  }

  // ─── Playback ──────────────────────────────────────────────────────

  async play() {
    if (!this._isPaused || !this._isReady) return;
    this._isPaused = false;

    // Must start from a keyframe after the previous flush
    const currentDecodeIdx = this._frameInfo[this._currentFrame]?.decodeIndex ?? 0;
    const kf = this._findKeyframeBefore(currentDecodeIdx);
    this._nextFeedIndex = kf;

    // Remote: pre-fetch reference chain + initial look-ahead
    if (this._isRemote) {
      const prefetchEnd = Math.min(
        currentDecodeIdx + this._maxBufferSize,
        this._samples.length - 1
      );
      await this._fetchSampleData(kf, prefetchEnd);
      if (this._isPaused) return; // paused during fetch
    }

    // Feed reference chain (keyframe → current)
    for (let i = kf; i <= currentDecodeIdx; i++) {
      this._feedSample(this._nextFeedIndex++);
    }

    // Pre-fill the buffer, then flush so frames are ready
    const prefillEnd = Math.min(
      this._nextFeedIndex + this._maxBufferSize,
      this._samples.length
    );
    for (let i = this._nextFeedIndex; i < prefillEnd; i++) {
      if (this._isRemote && !this._samples[i].data) break;
      this._feedSample(i);
      this._nextFeedIndex = i + 1;
    }

    await this._flushAndWait();

    if (this._isPaused) return;

    // After flush the decoder needs a keyframe. Rewind the feed cursor
    // so the render loop's first _topUpDecoder call starts from one.
    if (this._decoderNeedsKeyframe) {
      this._nextFeedIndex = this._findKeyframeBefore(this._nextFeedIndex);
    }

    // Wall clock
    this._playStartWallTime = performance.now();
    this._playStartPTS = this.currentTime;

    // Start background fetch-ahead for remote
    if (this._isRemote) {
      this._fetchAheadLoop(); // fire-and-forget
    }

    this._renderLoop();

    if (this.onPlay) this.onPlay();
  }

  pause() {
    if (this._isPaused) return;
    this._isPaused = true;

    if (this._rafId) { cancelAnimationFrame(this._rafId); this._rafId = null; }

    if (this.onTimeUpdate) this.onTimeUpdate(this.currentTime);
    if (this.onPause) this.onPause();
  }

  setPlaybackRate(rate) {
    this._playbackRate = rate;
    // Re-anchor the wall clock so the rate change takes effect immediately
    // without a jump in the computed targetTime.
    this._playStartWallTime = performance.now();
    this._playStartPTS = this.currentTime;
  }

  _renderLoop() {
    if (this._isPaused) return;

    const elapsed = (performance.now() - this._playStartWallTime) / 1000;
    const targetTime = this._playStartPTS + elapsed * this._playbackRate;
    const targetFrame = this.getFrameForTime(targetTime);

    // End of video?
    if (targetFrame >= this.frameCount - 1) {
      if (this._frameBuffer.has(this.frameCount - 1)) {
        this._displayFrame(this.frameCount - 1);
      }
      this.pause();
      if (this.onEnded) this.onEnded();
      return;
    }

    if (targetFrame > this._currentFrame) {
      // Display the latest available frame up to targetFrame.
      // Clock never stalls — if the decoder can't keep up we skip
      // missing frames.  Seeking / stepping still accesses every frame.
      let bestFrame = -1;
      for (let f = targetFrame; f > this._currentFrame; f--) {
        if (this._frameBuffer.has(f)) {
          bestFrame = f;
          break;
        }
      }
      if (bestFrame !== -1) {
        this._displayFrame(bestFrame);
      }
    }

    // Release frames far behind the playback head, keeping a trailing
    // buffer so backward stepping doesn't require re-decoding.
    const evictBefore = this._currentFrame - this._maxBufferBehind;
    for (const [idx, frame] of this._frameBuffer) {
      if (idx < evictBefore) {
        try { frame.close(); } catch (_) {}
        this._frameBuffer.delete(idx);
      }
    }

    // Feed decoder to stay ahead
    this._topUpDecoder();

    this._rafId = requestAnimationFrame(() => this._renderLoop());
  }

  // ─── Frame stepping ────────────────────────────────────────────────

  async stepForward(n = 1) {
    await this.seekToFrame(Math.min(this._currentFrame + n, this.frameCount - 1));
  }

  async stepBackward(n = 1) {
    await this.seekToFrame(Math.max(this._currentFrame - n, 0));
  }

  // ─── Frame index helpers ───────────────────────────────────────────

  getFrameForTime(time) {
    if (this.frameTimestamps.length === 0) return 0;
    let lo = 0, hi = this.frameTimestamps.length - 1;
    while (lo < hi) {
      const mid = Math.ceil((lo + hi) / 2);
      if (this.frameTimestamps[mid] <= time + 0.0001) lo = mid;
      else hi = mid - 1;
    }
    return lo;
  }

  getTimeForFrame(frame) {
    if (frame >= 0 && frame < this.frameTimestamps.length) {
      return this.frameTimestamps[frame];
    }
    return 0;
  }

  // ─── Cleanup ───────────────────────────────────────────────────────

  _clearBuffer() {
    for (const [, frame] of this._frameBuffer) {
      try { frame.close(); } catch (_) {}
    }
    this._frameBuffer.clear();
  }

  destroy() {
    this._isPaused = true;
    this._seekInProgress = false;
    this._pendingSeek = null;
    if (this._rafId) { cancelAnimationFrame(this._rafId); this._rafId = null; }
    this._clearBuffer();
    this._pendingBitmaps.clear();
    for (const [, waiter] of this._frameWaiters) {
      try { waiter(); } catch (_) {}
    }
    this._frameWaiters.clear();
    if (this._decoder && this._decoder.state !== 'closed') {
      try { this._decoder.close(); } catch (_) {}
    }
    this._samples = [];
    this.frameTimestamps = [];
    this._frameInfo = [];
    this._isReady = false;
    this._isRemote = false;
    this._remoteUrl = null;
  }
}
